import React, { Dispatch, PropsWithChildren, SetStateAction, createContext, useEffect, useState } from 'react';

import {
    Entity,
    GetAGGridEnterpriseLicenseOutput,
    GetEntitiesInput,
    GetServicesByCodeInput,
    GetTerritoriesByIdInput,
    IntegerList
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { LicenseManager } from 'ag-grid-enterprise';
import { Auth } from 'aws-amplify';
import { contractConfigurationClient } from 'src/api/client';
import { SiteUnavailable } from 'src/components/Redirects';
import { CM_UI_SUPPORT_SERVICE_ID_LIST } from 'src/constants/context';
import { Role } from 'src/constants/enums';
import { BREAKPOINT_SM } from 'src/constants/responsive';
import { AgentContextProvider } from 'src/contexts/AgentContext';
import { useCookie } from 'src/hooks/useCookie';
import { ICommentSheetAttributesProps } from 'src/types/ICommentSheetAttributes';
import { getCachedMetadata, setCachedMetadata, shouldRefreshCache } from 'src/util/metadataCache';
import { buildServiceMap, buildTerritoryMap } from 'src/util/metadataUtil';

import { IAppContext, IRightSideBarContent } from '../types/IAppContext';
import { IAppContextMetadata } from '../types/IMetadata';
import { IToast } from '../types/IToaster';
import { IUserInfo } from '../types/IUserInfo';

const defaultAppContextMetadata: IAppContextMetadata = {
    serviceMapping: {},
    territoryMapping: {}
};

interface IAppContextState extends IAppContext {
    setEntities: Dispatch<SetStateAction<Entity[] | undefined>>;
    entityRefreshCounter: number;
    setEntityRefreshCounter: Dispatch<SetStateAction<number>>;
}

export const defaultContextValue: IAppContextState = {
    toasts: [] as IToast[],
    setToasts: () => {},
    topAlertMsg: '',
    setTopAlertMsg: () => {},
    metadata: defaultAppContextMetadata,
    setMetadata: () => {},
    userRole: Role.None,
    setUserRole: () => {},
    userInfo: { username: 'unknown' },
    setUserInfo: () => {},
    commentSheetAttributes: {} as ICommentSheetAttributesProps,
    setCommentSheetAttributes: () => {},
    commentStatus: [],
    setCommentStatus: () => {},
    isSideBarClosed: false,
    setIsSideBarClosed: () => {},
    isRightSideBarOpen: false,
    setIsRightSideBarOpen: () => {},
    rightSideBarContent: {} as IRightSideBarContent,
    setRightSideBarContent: () => {},
    entities: undefined,
    setEntities: () => {},
    entityRefreshCounter: 0,
    setEntityRefreshCounter: () => {},
    showDealsRepo: false,
    setShowDealsRepo: () => {},
    analyticsFeatureEnabled: false,
    setAnalyticsFeatureEnabled: () => {},
    royaltyManagerFeatureEnabled: false,
    setRoyaltyManagerFeatureEnabled: () => {}
};

export const AppContext = createContext<IAppContext>(defaultContextValue);

export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [toasts, setToasts] = useState<IToast[]>([]);
    const [topAlertMsg, setTopAlertMsg] = useState('');
    const [metadata, setMetadata] = useState<IAppContextMetadata>(defaultAppContextMetadata);
    const [userRole, setUserRole] = useState(Role.None);
    const [userInfo, setUserInfo] = useState({} as IUserInfo);
    const [entities, setEntities] = useState<Entity[] | undefined>();
    const [entityRefreshCounter, setEntityRefreshCounter] = useState<number>(0);

    const [isMetadataLoaded, setIsMetadataLoaded] = useState<boolean>(false);
    const [isLicenseLoaded, setIsLicenseLoaded] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [commentSheetAttributes, setCommentSheetAttributes] = useState<ICommentSheetAttributesProps>({
        openCommentSheet: false,
        categoryUniqueColumnId: null,
        highlightedButton: null
    });
    const [commentStatus, setCommentStatus] = useState<IntegerList>([]);

    const [isSideBarClosed, setIsSideBarClosed] = useState<boolean>(
        window.innerWidth < parseInt(BREAKPOINT_SM.slice(0, BREAKPOINT_SM.length - 2))
    );

    const [isRightSideBarOpen, setIsRightSideBarOpen] = useState(false);
    const [rightSideBarContent, setRightSideBarContent] = useState({} as IRightSideBarContent);

    const [showDealsRepo, setShowDealsRepo] = useCookie('dealsRepo', false);
    const [analyticsFeatureEnabled, setAnalyticsFeatureEnabled] = useCookie('analyticsFeatureEnabled', false);
    const [royaltyManagerFeatureEnabled, setRoyaltyManagerFeatureEnabled] = useCookie(
        'royaltyManagerFeatureEnabled',
        false
    );

    useEffect(() => {
        // Check cache for license first
        const cachedData = getCachedMetadata();

        if (cachedData && !shouldRefreshCache(cachedData) && cachedData.agGridLicense) {
            // Use cached license
            LicenseManager.setLicenseKey(cachedData.agGridLicense);
            setIsLicenseLoaded(true);
        } else {
            // Fetch fresh license
            contractConfigurationClient.getAGGridEnterpriseLicense().subscribe(
                (response: GetAGGridEnterpriseLicenseOutput) => {
                    const license = response.agGridEnterpriseLicense!;
                    LicenseManager.setLicenseKey(license);
                    setIsLicenseLoaded(true);

                    // Update cache with license
                    const currentCache = getCachedMetadata();
                    if (currentCache) {
                        setCachedMetadata(currentCache.data, license);
                    }
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('GetAGGridEnterpriseLicense Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                    setIsLicenseLoaded(true); // Don't block app on license failure
                }
            );
        }

        callMetadataAPIs();
    }, []);

    useEffect(() => {
        Auth.currentSession()
            .then((userSession) => {
                const userPayload = userSession.getIdToken().decodePayload();
                const userLDAPGroupList = JSON.parse(userPayload['custom:access_groups']);
                const userANTGroupList = JSON.parse(userPayload['custom:test_access_groups']);

                if (userLDAPGroupList.includes(Role.Admin) || userANTGroupList.includes(Role.Tester)) {
                    setUserRole(Role.Admin);
                } else if (userLDAPGroupList.includes(Role.Writer)) {
                    setUserRole(Role.Writer);
                } else if (userLDAPGroupList.includes(Role.Editor)) {
                    setUserRole(Role.Editor);
                } else if (userLDAPGroupList.includes(Role.CAM)) {
                    setUserRole(Role.CAM);
                } else if (userLDAPGroupList.includes(Role.Maple)) {
                    setUserRole(Role.Maple);
                } else if (userLDAPGroupList.includes(Role.Viewer)) {
                    setUserRole(Role.Viewer);
                }

                setUserInfo({
                    username: userPayload.identities[0].userId,
                    firstName: userPayload.given_name
                });
            })
            .catch((error: Error) => {
                // eslint-disable-next-line no-console
                console.error('Retrieve user information and LDAP Group list Unexpected error', error.message);
                setTopAlertMsg(error.message);
            });
    }, []);

    const callMetadataAPIs = async () => {
        try {
            // Check cache first
            const cachedData = getCachedMetadata();
            const cachedLicense = cachedData?.agGridLicense;

            if (cachedData && !shouldRefreshCache(cachedData)) {
                // Use cached data
                setMetadata(cachedData.data);
                setIsMetadataLoaded(true);
                setIsLoading(false);
                return;
            }

            // Fetch fresh data
            const servicesPromise = contractConfigurationClient
                .getServicesByCode(GetServicesByCodeInput.clone({}))
                .toPromise();
            const territoriesPromise = contractConfigurationClient
                .getTerritoriesById(
                    GetTerritoriesByIdInput.clone({
                        includeUnlaunchedTerritories: true
                    })
                )
                .toPromise();

            const [servicesResponse, territoriesResponse] = await Promise.all([servicesPromise, territoriesPromise]);

            const metadata = {
                serviceMapping: buildServiceMap(servicesResponse.services ?? []),
                territoryMapping: buildTerritoryMap(territoriesResponse.territories ?? [])
            };

            // Cache the data with ag grid license
            const cached = setCachedMetadata(metadata, cachedLicense);
            if (!cached) {
                console.error('Failed to cache metadata');
            }

            setMetadata(metadata);
            setIsMetadataLoaded(true);
        } catch (error) {
            const coralError = error as UnknownCoralError;
            // eslint-disable-next-line no-console
            console.error('Metadata APIs Unexpected error', coralError.receivedException?.message || error);
            setTopAlertMsg(coralError.receivedException?.message || 'Failed to load metadata');

            // Try to use cached data as fallback even if expired
            const cachedData = getCachedMetadata();
            if (cachedData) {
                setMetadata(cachedData.data);
                setIsMetadataLoaded(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        function updateEntities(prev: Entity[] | undefined, next: Entity[] | undefined) {
            const existing = new Map(prev?.map((entity) => [entity.entityId!, entity]) ?? []);

            next?.forEach((entity) => {
                const entry: Entity = existing.get(entity.entityId!) ?? {
                    ...entity,
                    entityVendorCodes: []
                };

                entry.entityVendorCodes?.push(...(entity.entityVendorCodes ?? []));

                existing.set(entity.entityId!, entry);
            });

            return Array.from(existing.values());
        }

        function getEntities(serviceId: number) {
            contractConfigurationClient
                .getEntitiesByIds(
                    GetEntitiesInput.clone({
                        entityType: 1,
                        serviceIds: [serviceId],
                        includeIneligibleVendorCodes: true
                    })
                )
                .subscribe({
                    next: (value) => setEntities((prev) => updateEntities(prev, value.entities)),
                    error: (e) => console.error(JSON.stringify(e))
                });
        }

        CM_UI_SUPPORT_SERVICE_ID_LIST.forEach((serviceId) => getEntities(serviceId));
    }, [entityRefreshCounter]);

    const contextValue: IAppContextState = {
        toasts,
        setToasts,
        topAlertMsg,
        setTopAlertMsg,
        metadata,
        setMetadata,
        userRole,
        setUserRole,
        userInfo,
        setUserInfo,
        commentSheetAttributes,
        setCommentSheetAttributes,
        commentStatus,
        setCommentStatus,
        isSideBarClosed,
        setIsSideBarClosed,
        isRightSideBarOpen,
        setIsRightSideBarOpen,
        rightSideBarContent,
        setRightSideBarContent,
        analyticsFeatureEnabled,
        setAnalyticsFeatureEnabled,
        royaltyManagerFeatureEnabled,
        setRoyaltyManagerFeatureEnabled,
        entities,
        setEntities,
        entityRefreshCounter,
        setEntityRefreshCounter,
        showDealsRepo,
        setShowDealsRepo
    };

    return (
        <AppContext.Provider value={contextValue}>
            <AgentContextProvider>
                {/*
                    TODO: We shouldn't block the whole site for random API calls
                    TODO: Unfortunately there are a bunch of pages which assume that metadata is loaded
                    TODO: before they are rendered, so changing this breaks a lot of things
                 */}
                {isLoading || !isLicenseLoaded ? (
                    <Row width="100%" height="100vh" alignmentHorizontal="center">
                        <Column alignmentHorizontal="center" alignmentVertical="center">
                            <Loader />
                            <Text type="b200" color="secondary">
                                Loading metadata...
                            </Text>
                        </Column>
                    </Row>
                ) : isMetadataLoaded ? (
                    children
                ) : (
                    <SiteUnavailable />
                )}
            </AgentContextProvider>
        </AppContext.Provider>
    );
};
